import { BeneficialEntity } from "../entities";

export enum SegmentedTextType {
	STATIC = 0,
	PARAM = 1,
	COMMENT = 2,
	PARAM_VALUE = 3,
	PARAM_TABLE_VALUE = 4,
	PARAM_COMMENT_VALUE = 5,
	PARAM_START = 6,
	PARAM_END = 7,
	STATIC_TABLE_START = 9,
	STATIC_TABLE_END = 10,         // New type for table end
	STATIC_TABLE_ROW_START = 11,   // New type for row start
	STATIC_TABLE_ROW_END = 12,     // New type for row end
	STATIC_TABLE_CELL_START = 13,  // New type for cell start
	STATIC_TABLE_CELL_END = 14,     // New type for cell end
	PARAGRAPH_START = 15, 				// New type for paragraph start
	LIST_ITEM_START = 17
}


export type segmentedTextStyle = {
	"bold"?: string,
	"italic"?: string,
	"underline"?: string,
	"textAlign"?: string,
	"listType"?: string,
	"borderColor"?: string,
}

export type SegmentedText = [string, string, SegmentedTextType, segmentedTextStyle?][]
type BasicSegment = {
	type: SegmentedTextType.STATIC
	| SegmentedTextType.COMMENT
	| SegmentedTextType.PARAGRAPH_START
	| SegmentedTextType.LIST_ITEM_START;
	id: string;
	value: string;
	style?: segmentedTextStyle;
}

type ParamValueSegment = {
	type: SegmentedTextType.PARAM
	| SegmentedTextType.PARAM_VALUE
	| SegmentedTextType.PARAM_COMMENT_VALUE
	| SegmentedTextType.PARAM_TABLE_VALUE
	| SegmentedTextType.PARAM_START
	| SegmentedTextType.PARAM_END
	| SegmentedTextType.STATIC_TABLE_START
	| SegmentedTextType.STATIC_TABLE_END
	| SegmentedTextType.STATIC_TABLE_ROW_START
	| SegmentedTextType.STATIC_TABLE_ROW_END
	| SegmentedTextType.STATIC_TABLE_CELL_END;
	id: string;
	value: string;
	paramName: string;
	inputValue?: any;
	style?: segmentedTextStyle;
}
export type ClauseParam = AnyParam
    | BeneficialParam
    | BeneficialListParam
    | BooleanParam
    | EnumParam
    | ListParam
    | TableParam
    | CSVParam
    | PropertyParam

export type ClauseParams = ClauseParam[]

type CellStartSegment = {
	type: SegmentedTextType.STATIC_TABLE_CELL_START;
	id: string;
	value: string;
	paramName: string;
	style?: segmentedTextStyle;
	rowIdx?: number;
	cellIdx?: number;
}

export type RenderSegment = BasicSegment
	| ParamValueSegment | CellStartSegment

export type RenderSegments = RenderSegment[]

type BaseParam =
	{
		name: string,
		label: string,
		formula?: string,
		format?:string,
		constraints?: Constraint[];
	}

type AnyParam = BaseParam &
{
	type: 'string' | 'date' | 'file' | 'comment'
}

export type Constraint = {
	label: string;
	value: string;
}

export type NumberParam = BaseParam &
{
	type: 'number';
	formula?: string;
	format?:string;
}

export type IndexParam = BaseParam &
{
	type: 'index',
	args: { start: number, step: number }
}

export type BeneficialParam = BaseParam &
{
	args: { beneficialTypes: (BeneficialEntity['type'])[] },
	type: 'beneficial'
}

export type PropertyParam = BaseParam &
{
	type: 'property'
	args: {
		tag: string,
		parentParam: string,
	},
}

export type StaticTableParam = BaseParam &
{
	args: { 
		cells: { content: string, cellStyle: segmentedTextStyle }[][],
		style: segmentedTextStyle
	 },
	type: 'static-table'
}

export type SegmentedStaticTableParam = BaseParam &
{
	args: { 
		cells: { content: SegmentedText, cellStyle: { [key: string]: string} }[][],
		style: segmentedTextStyle
	 },
	type: 'static-table',
	definition: number
}

export type SegmentedBeneficialParam = BaseParam &
{
	args: { beneficialTypes: (BeneficialEntity['type'])[] },
	type: 'beneficial'
}

export type BeneficialListParam = BaseParam &
{
	args: { beneficialTypes: (BeneficialEntity['type'])[] },
	type: 'beneficial[]'
}

export type SegmentedBeneficialListParam = BaseParam &
{
	args: { beneficialTypes: (BeneficialEntity['type'])[], },
	type: 'beneficial[]'
}

export type BooleanParam = BaseParam &
{
	args: { textIfTrue: string, textIfFalse: string },
	definition: number;
	type: 'boolean'
}

export type SegmentedBooleanParam = BaseParam &
{
	args: { textIfTrue: SegmentedText, textIfFalse: SegmentedText },
	definition: number;
	type: 'boolean'
}

export type EnumParam = BaseParam &
{
	args: { option: string, text: string }[],
	definition: number;
	type: 'enum'
}
export type EnumOption = {
	option: string, text: string
}
export type ListOption = {
	option: string, required: boolean
}

export type SegmentedEnumParam = BaseParam &
{
	args: { option: string, text: SegmentedText }[],
	definition: number;
	type: 'enum'
}

export type ListParam = BaseParam &
{
	args: { option: string, required: boolean }[],
	type: 'list'
}

export type CSVParam = BaseParam &
{
	type: 'csv'
}

export type TableParam = BaseParam &
{
	transposed: boolean,
	args: { header: string, type: 'string' | 'date' | 'number' }[],
	type: 'table'
}
export type TableLigne = {
	header: string,
	type: 'string' | 'date' | 'number'
}

export type SegmentedClauseParam = AnyParam
	| NumberParam
	| SegmentedBeneficialParam
	| SegmentedBeneficialListParam
	| SegmentedBooleanParam
	| SegmentedEnumParam
	| ListParam
	| TableParam
	| CSVParam
	| PropertyParam
	| SegmentedStaticTableParam
	| IndexParam

export type SegmentedClauseParams = SegmentedClauseParam[]
